import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import {
  Alert,
  App,
  Button,
  Col,
  Drawer,
  Form,
  Input,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Table,
  Tag,
} from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  ActivationStatus,
  Customer,
  CustomerAuthorization,
  CustomerContact,
  CustomerDuplicateCheckResponse,
} from '../../../api/active/generated/customer';
import {
  CustomerApiError,
  createIdempotencyKey,
} from '../../../customer-integration/customerApi';
import { useCustomerPageRuntime } from '../../../customer-integration/customerPageRuntime';
import {
  BusinessCustomFields,
  type BusinessFieldValue,
  formatBusinessFieldValue,
  useBusinessFields,
} from '../../../equipment-integration/BusinessCustomFields';
import { useFieldDisplayNames } from '../../../equipment-integration/useFieldDisplayNames';
import styles from '../styles.module.css';
import { ManagementFilterBar } from './ManagementFilterBar';

type DrawerMode = 'create' | 'view' | 'edit';
type CustomerDraft = {
  name: string;
  status: ActivationStatus;
  contactName?: string;
  contactPhone?: string;
};
type ContactDraft = {
  name: string;
  phone?: string;
  status: ActivationStatus;
  primary: boolean;
};
const PHONE = /^\+?[0-9 -]{7,24}$/;
const isFormValidationError = (cause: unknown) =>
  typeof cause === 'object' && cause !== null && 'errorFields' in cause;

export function CustomersPage() {
  return <CustomersPageContent />;
}

export function CustomersPageContent() {
  const { message } = App.useApp();
  const { api, identity, canManage, recoverUnauthorized } =
    useCustomerPageRuntime();
  const customerFields = useBusinessFields('customer');
  const displayNames = useFieldDisplayNames();
  const customerLabel = (fieldKey: string, fallback: string) =>
    displayNames.label('customer', 'customer', fieldKey, fallback);
  const [form] = Form.useForm<CustomerDraft>();
  const [contactForm] = Form.useForm<ContactDraft>();
  const [items, setItems] = useState<Customer[]>([]);
  const [createAuthorization, setCreateAuthorization] = useState<
    CustomerAuthorization | undefined
  >();
  const phoneWritable = (authorization: CustomerAuthorization | undefined) =>
    canManage &&
    authorization?.fieldCapabilities.some(
      (field) =>
        field.fieldKey === 'contact_phone' &&
        field.writable &&
        field.writeDecision === 'allow',
    ) === true;
  const canCreatePhoneWrite = phoneWritable(createAuthorization);

  const [page, setPage] = useState(1);
  const pageSize = 20;
  const [total, setTotal] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [listStatus, setListStatus] = useState<
    'loading' | 'ready' | 'empty' | 'error'
  >('loading');
  const [errorText, setErrorText] = useState('');
  const revision = useRef(0);
  // Every open/close starts a distinct drawer. Requests may still finish, but
  // their result belongs only to the drawer that initiated them.
  const drawerRevision = useRef(0);
  const [mode, setMode] = useState<DrawerMode | null>(null);
  const [detail, setDetail] = useState<Customer | null>(null);
  const canDetailPhoneWrite = phoneWritable(detail?.authorization);
  const [customFields, setCustomFields] = useState<BusinessFieldValue[]>([]);
  const [customFieldsDirty, setCustomFieldsDirty] = useState(false);
  const [etag, setEtag] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [deletingCustomerId, setDeletingCustomerId] = useState<string | null>(
    null,
  );
  const latestList = useRef({ items, page });
  latestList.current = { items, page };
  const [duplicates, setDuplicates] = useState<
    CustomerDuplicateCheckResponse['items']
  >([]);
  const [duplicateConfirmed, setDuplicateConfirmed] = useState(false);
  const [contactId, setContactId] = useState<'new' | string | null>(null);
  const showError = useCallback(
    (cause: unknown) => {
      const error =
        cause instanceof CustomerApiError
          ? cause
          : new CustomerApiError(
              null,
              undefined,
              cause instanceof Error ? cause.message : '客户请求失败',
            );
      const text =
        error.status === 401
          ? '会话已失效，请重新登录。'
          : error.status === 403
            ? '当前身份无权执行此操作。'
            : error.status === 404
              ? '客户不存在或已不可访问。'
              : error.status === 409
                ? '数据已被其他操作更新，请重新加载最新详情；当前草稿已保留。'
                : error.status === 400 || error.status === 422
                  ? `提交内容未通过校验：${error.message}`
                  : error.message;
      setErrorText(text);
      if (error.status === 401) void recoverUnauthorized();
      return error;
    },
    [recoverUnauthorized],
  );
  const load = useCallback(async () => {
    const current = ++revision.current;
    setListStatus('loading');
    setErrorText('');
    try {
      const result = await api.list(page, pageSize, search);
      if (current !== revision.current) return;
      setItems(result.items);
      setCreateAuthorization(result.createAuthorization);
      setTotal(result.meta.total);
      setListStatus(result.items.length ? 'ready' : 'empty');
    } catch (cause) {
      if (current !== revision.current) return;
      showError(cause);
      setCreateAuthorization(undefined);
      setListStatus('error');
    }
  }, [api, page, search, showError]);
  const latestLoad = useRef(load);
  latestLoad.current = load;
  useEffect(
    () => () => {
      drawerRevision.current += 1;
      revision.current += 1;
    },
    [],
  );
  useEffect(() => {
    void load();
  }, [load]);
  useEffect(() => {
    if (mode === 'create') {
      form.setFieldsValue({
        name: '',
        status: 'active',
        contactName: '',
        contactPhone: '',
      });
      setCustomFields([]);
      setCustomFieldsDirty(false);
    } else if (mode && detail) {
      form.setFieldsValue({
        name: detail.name,
        status: detail.status,
      });
      setCustomFields(
        (detail as Customer & { customFields?: BusinessFieldValue[] })
          .customFields ?? [],
      );
      setCustomFieldsDirty(false);
    }
  }, [detail, form, mode]);

  const close = () => {
    drawerRevision.current += 1;
    setSubmitting(false);
    setMode(null);
    setDetail(null);
    setCustomFields([]);
    setCustomFieldsDirty(false);
    setEtag('');
    setDuplicates([]);
    setDuplicateConfirmed(false);
    setContactId(null);
    setErrorText('');
    form.resetFields();
  };
  const deleteCustomer = async (row: Customer) => {
    setDeletingCustomerId(row.id);
    setErrorText('');
    try {
      const latest = await api.get(row.id);
      await api.delete(row.id, latest.etag);
      message.success('客户已删除');
      const current = latestList.current;
      if (
        current.items.some((item) => item.id === row.id) &&
        current.items.length === 1 &&
        current.page > 1
      ) {
        setPage((selected) => Math.max(1, selected - 1));
      } else {
        await latestLoad.current();
      }
    } catch (cause) {
      showError(cause);
    } finally {
      setDeletingCustomerId(null);
    }
  };
  const openCreate = () => {
    drawerRevision.current += 1;
    setSubmitting(false);
    setContactId(null);
    setErrorText('');
    if (mode === 'create') form.resetFields();
    setMode('create');
    setDetail(null);
    setCustomFields([]);
    setCustomFieldsDirty(false);
    setEtag('');
    setDuplicates([]);
    setDuplicateConfirmed(false);
  };
  const openDetail = async (
    id: string,
    nextMode: Exclude<DrawerMode, 'create'>,
  ) => {
    const current = ++drawerRevision.current;
    setContactId(null);
    setSubmitting(true);
    setErrorText('');
    setDuplicates([]);
    setDuplicateConfirmed(false);
    try {
      const result = await api.get(id);
      if (current !== drawerRevision.current) return;
      setDetail(result.customer);
      setCustomFields(
        (result.customer as Customer & { customFields?: BusinessFieldValue[] })
          .customFields ?? [],
      );
      setCustomFieldsDirty(false);
      setEtag(result.etag);
      setMode(nextMode);
    } catch (cause) {
      if (current === drawerRevision.current) showError(cause);
    } finally {
      if (current === drawerRevision.current) setSubmitting(false);
    }
  };
  const confirmWrite = async (
    result: { customer: Customer; etag: string },
    current: number,
  ) => {
    // The write remains valid after its drawer closes. Refresh the currently
    // selected list, never the search/page captured when the write started.
    const refresh = latestLoad.current();
    if (current !== drawerRevision.current) {
      await refresh;
      return;
    }
    setDetail(result.customer);
    setCustomFields(
      (result.customer as Customer & { customFields?: BusinessFieldValue[] })
        .customFields ?? [],
    );
    setCustomFieldsDirty(false);
    setEtag(result.etag);
    await refresh;
    if (current !== drawerRevision.current) return;
    try {
      const reread = await api.get(result.customer.id);
      if (current !== drawerRevision.current) return;
      setDetail(reread.customer);
      setCustomFields(
        (reread.customer as Customer & { customFields?: BusinessFieldValue[] })
          .customFields ?? [],
      );
      setCustomFieldsDirty(false);
      setEtag(reread.etag);
      message.success('已保存并从服务端回读确认');
    } catch {
      if (current !== drawerRevision.current) return;
      message.warning('保存成功，但详情回读尚未确认；请重新加载详情');
    }
  };

  const saveCustomer = async (forceDuplicate = false) => {
    if (!canManage || submitting) return;
    const current = drawerRevision.current;
    try {
      const values = await form.validateFields();
      if (current !== drawerRevision.current) return;
      const contactPhone = canCreatePhoneWrite
        ? (values.contactPhone?.trim() ?? '')
        : '';
      setSubmitting(true);
      setErrorText('');
      if (mode === 'create' && !forceDuplicate && !duplicateConfirmed) {
        const checked = await api.duplicate({
          name: values.name.trim(),
          phone: contactPhone || undefined,
        });
        if (current !== drawerRevision.current) return;
        if (checked.items.length) {
          setDuplicates(checked.items);
          return;
        }
      }
      const ownership = {
        ownerUserIds: [identity.id],
        primaryOwnerUserId: identity.id,
        departmentId: identity.primaryDepartmentId,
      };
      const key = createIdempotencyKey();
      const result =
        mode === 'create'
          ? await api.create(
              {
                name: values.name.trim(),
                status: values.status,
                ...ownership,
                ...(customFieldsDirty ? { customFields } : {}),
                ...(values.contactName?.trim() || contactPhone
                  ? {
                      primaryContact: {
                        name: values.contactName?.trim() ?? '',
                        phone: contactPhone,
                        primary: true as const,
                      },
                    }
                  : {}),
              },
              key,
            )
          : detail
            ? await api.update(
                detail.id,
                etag,
                {
                  name: values.name.trim(),
                  status: values.status,
                  ...(customFieldsDirty ? { customFields } : {}),
                },
                key,
              )
            : null;
      if (result) {
        await confirmWrite(result, current);
        if (current === drawerRevision.current && mode === 'create')
          setMode('edit');
      }
    } catch (cause) {
      if (current === drawerRevision.current && !isFormValidationError(cause))
        showError(cause);
    } finally {
      if (current === drawerRevision.current) setSubmitting(false);
    }
  };

  const beginContact = (contact?: CustomerContact) => {
    setContactId(contact?.id ?? 'new');
  };
  useEffect(() => {
    if (!contactId) return;
    const contact = detail?.contacts.find((item) => item.id === contactId);
    contactForm.setFieldsValue(
      contact
        ? {
            name: contact.name,
            phone: contact.phoneAvailability === 'full' ? contact.phone : '',
            status: contact.status,
            primary: contact.primary,
          }
        : { name: '', phone: '', status: 'active', primary: false },
    );
  }, [contactForm, contactId, detail]);
  const saveContact = async () => {
    if (!detail || !canManage || !contactId || submitting) return;
    if (contactId === 'new' && !canDetailPhoneWrite) return;
    const current = drawerRevision.current;
    try {
      const values = await contactForm.validateFields();
      if (current !== drawerRevision.current) return;
      setSubmitting(true);
      setErrorText('');
      const key = createIdempotencyKey();
      const existing =
        contactId === 'new'
          ? null
          : (detail.contacts.find((item) => item.id === contactId) ?? null);
      const result =
        contactId === 'new'
          ? await api.addContact(
              detail.id,
              etag,
              {
                name: values.name.trim(),
                phone: values.phone?.trim() ?? '',
                primary: false,
              },
              key,
            )
          : await api.updateContact(
              detail.id,
              contactId,
              etag,
              {
                ...(values.name.trim() ? { name: values.name.trim() } : {}),
                status: values.status,
                ...(canDetailPhoneWrite && values.phone?.trim()
                  ? { phone: values.phone.trim() }
                  : existing?.phoneAvailability === 'masked'
                    ? {}
                    : {}),
              },
              key,
            );
      await confirmWrite(result, current);
      if (current !== drawerRevision.current) return;
      setContactId(null);
      contactForm.resetFields();
    } catch (cause) {
      if (current === drawerRevision.current && !isFormValidationError(cause))
        showError(cause);
    } finally {
      if (current === drawerRevision.current) setSubmitting(false);
    }
  };
  const setPrimary = async (targetId: string) => {
    if (!detail || !canManage || submitting) return;
    const current = drawerRevision.current;
    try {
      setSubmitting(true);
      setErrorText('');
      await confirmWrite(
        await api.setPrimary(detail.id, targetId, etag, createIdempotencyKey()),
        current,
      );
    } catch (cause) {
      if (current === drawerRevision.current) showError(cause);
    } finally {
      if (current === drawerRevision.current) setSubmitting(false);
    }
  };
  const submitSearch = () => {
    setPage(1);
    setSearch(searchInput.trim());
  };

  return (
    <div className={styles.managementPage}>
      <ManagementFilterBar
        label="客户管理筛选与操作"
        fieldsetAttributes={{ 'data-customer-baseline': 'filter-bar' }}
        filters={
          <>
            <Input.Search
              aria-label="客户搜索"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              onSearch={submitSearch}
              placeholder="名称 / 编号 / 联系人 / 电话"
            />
            <span
              className={styles.customerDeferredFilterSlot}
              data-deferred-slot="manager-filter"
              aria-hidden="true"
            />
            <span
              className={styles.customerDeferredFilterSlot}
              data-deferred-slot="quote-count-filter"
              aria-hidden="true"
            />
            <span
              className={styles.customerDeferredFilterSlot}
              data-deferred-slot="recent-quote-filter"
              aria-hidden="true"
            />
          </>
        }
        actions={
          <>
            <Button
              onClick={() => {
                setSearchInput('');
                setSearch('');
                setPage(1);
              }}
            >
              重置
            </Button>
            <Button type="primary" onClick={submitSearch}>
              查询
            </Button>
            <Space data-customer-baseline="header-actions" size={8} wrap>
              <Button
                onClick={() => void load()}
                loading={listStatus === 'loading'}
              >
                刷新
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                disabled={!canManage}
                onClick={openCreate}
              >
                新增客户
              </Button>
            </Space>
          </>
        }
      />
      {errorText && mode === null ? (
        <Alert
          type="error"
          showIcon
          title={errorText}
          action={<Button onClick={() => void load()}>重试</Button>}
        />
      ) : null}
      <Table<Customer>
        data-customer-baseline="small-table"
        rowKey="id"
        size="small"
        loading={listStatus === 'loading'}
        dataSource={items}
        locale={{
          emptyText:
            listStatus === 'empty'
              ? '暂无客户'
              : listStatus === 'error'
                ? '客户目录加载失败'
                : '正在加载',
        }}
        pagination={{
          current: page,
          pageSize,
          total,
          onChange: setPage,
          showTotal: (value) => `共 ${value} 条`,
        }}
        columns={[
          {
            title: customerLabel('customer_no', '客户编号'),
            dataIndex: 'customerNo',
          },
          {
            title: customerLabel('customer_name', '客户名称'),
            dataIndex: 'name',
          },
          {
            title: customerLabel('customer_contact', '联系人'),
            render: (_, row) =>
              row.contacts.find((item) => item.primary)?.name ?? '-',
          },
          {
            title: customerLabel('customer_phone', '联系电话'),
            render: (_, row) =>
              row.contacts.find((item) => item.primary)?.phone ?? '-',
          },
          {
            title: customerLabel('customer_manager', '归属客户经理'),
            render: (_, row) => row.primaryOwnerName || '—',
          },
          {
            title: customerLabel('customer_status', '状态'),
            render: (_, row) => (
              <Tag color={row.status === 'active' ? 'green' : 'default'}>
                {row.status === 'active' ? '启用' : '停用'}
              </Tag>
            ),
          },
          ...customerFields.definitions.map((definition) => ({
            title: definition.labelZh,
            key: `custom:${definition.id}`,
            render: (_: unknown, row: Customer) => {
              const value = (
                row as Customer & { customFields?: BusinessFieldValue[] }
              ).customFields?.find(
                (field) => field.definitionId === definition.id,
              )?.value;
              return formatBusinessFieldValue(definition, value);
            },
          })),
          {
            title: '操作',
            render: (_, row) => (
              <Space>
                <Button
                  type="link"
                  onClick={() => void openDetail(row.id, 'view')}
                >
                  查看
                </Button>
                <Button
                  type="link"
                  icon={<EditOutlined />}
                  disabled={!canManage}
                  onClick={() => void openDetail(row.id, 'edit')}
                >
                  编辑
                </Button>
                {row.authorization?.allowedActions.includes(
                  'CUSTOMER_DELETE',
                ) ? (
                  <Popconfirm
                    title="删除客户"
                    description="客户会从列表和新业务选择中移除，历史报价、订单和联系人数据保留。"
                    okText="删除"
                    cancelText="取消"
                    okButtonProps={{ danger: true }}
                    onConfirm={() => void deleteCustomer(row)}
                  >
                    <Button
                      danger
                      type="link"
                      icon={<DeleteOutlined />}
                      loading={deletingCustomerId === row.id}
                    >
                      删除
                    </Button>
                  </Popconfirm>
                ) : null}
              </Space>
            ),
          },
        ]}
      />
      <Drawer
        data-customer-baseline="drawer-600"
        open={mode !== null}
        size={600}
        onClose={close}
        destroyOnHidden
        title={
          mode === 'create'
            ? '新增客户'
            : mode === 'view'
              ? `查看客户 · ${detail?.name ?? ''}`
              : `编辑客户 · ${detail?.name ?? ''}`
        }
        footer={
          <Space>
            <Button onClick={close}>{mode === 'view' ? '关闭' : '取消'}</Button>
            {detail ? (
              <Button
                onClick={() =>
                  void openDetail(detail.id, mode === 'view' ? 'view' : 'edit')
                }
              >
                重新加载最新详情
              </Button>
            ) : null}
            {mode !== 'view' ? (
              <Button
                type="primary"
                loading={submitting}
                disabled={!canManage}
                onClick={() => void saveCustomer()}
              >
                保存
              </Button>
            ) : null}
          </Space>
        }
      >
        {errorText ? (
          <Alert
            type="error"
            showIcon
            title={errorText}
            style={{ marginBottom: 16 }}
          />
        ) : null}
        <Form
          name="customer-detail"
          form={form}
          layout="vertical"
          requiredMark
          disabled={mode === 'view'}
        >
          <Row gutter={16} data-customer-baseline="drawer-row">
            <Col xs={24} sm={12}>
              <Form.Item
                label={customerLabel('customer_name', '客户名称')}
                name="name"
                rules={[{ required: true, whitespace: true, max: 200 }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label={customerLabel('customer_no', '客户编号')}>
                <Input
                  value={detail?.customerNo ?? '创建后由服务端生成'}
                  disabled
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label={customerLabel('customer_status', '状态')}
                name="status"
                rules={[{ required: true }]}
              >
                <Select
                  options={[
                    { value: 'active', label: '启用' },
                    { value: 'inactive', label: '停用' },
                  ]}
                />
              </Form.Item>
            </Col>
            {mode === 'create' ? (
              <>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label={customerLabel('customer_contact', '主联系人')}
                    name="contactName"
                  >
                    <Input />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label={customerLabel('customer_phone', '联系电话')}
                    name="contactPhone"
                    extra={
                      !canCreatePhoneWrite
                        ? '当前电话字段不可编辑，可仅填写联系人姓名'
                        : undefined
                    }
                    rules={[
                      {
                        pattern: PHONE,
                        message:
                          '电话需为 7-24 位数字、空格或短横线，可含国际区号',
                      },
                    ]}
                  >
                    <Input disabled={!canCreatePhoneWrite} />
                  </Form.Item>
                </Col>
              </>
            ) : null}
          </Row>
        </Form>
        {customerFields.error ? (
          <Alert type="error" title={customerFields.error} />
        ) : null}
        <BusinessCustomFields
          definitions={customerFields.definitions}
          values={customFields}
          onChange={(values) => {
            setCustomFields(values);
            setCustomFieldsDirty(true);
          }}
          disabled={mode === 'view' || !canManage}
        />
        {detail ? (
          <>
            <h3>联系人</h3>
            {mode !== 'view' ? (
              <Button
                icon={<PlusOutlined />}
                disabled={!canManage || !canDetailPhoneWrite || submitting}
                onClick={() => beginContact()}
              >
                新增联系人
              </Button>
            ) : null}
            {mode !== 'view' && canManage && !canDetailPhoneWrite ? (
              <Alert
                type="info"
                showIcon
                title="当前电话字段不可编辑，新增联系人需要姓名和电话；仍可编辑已有联系人的其他信息"
              />
            ) : null}
            <Table<CustomerContact>
              rowKey="id"
              size="small"
              pagination={false}
              dataSource={detail.contacts}
              columns={[
                {
                  title: customerLabel('customer_contact', '姓名'),
                  dataIndex: 'name',
                },
                {
                  title: customerLabel('customer_phone', '电话'),
                  dataIndex: 'phone',
                },
                {
                  title: customerLabel('customer_contact_status', '状态'),
                  render: (_, row) =>
                    row.status === 'active' ? '启用' : '停用',
                },
                {
                  title: customerLabel('customer_primary_contact', '主联系人'),
                  render: (_, row) => (row.primary ? '是' : '否'),
                },
                ...(mode !== 'view'
                  ? [
                      {
                        title: '操作',
                        render: (_: unknown, row: CustomerContact) => (
                          <Space>
                            <Button
                              type="link"
                              onClick={() => beginContact(row)}
                            >
                              编辑/启停
                            </Button>
                            {!row.primary ? (
                              <Button
                                type="link"
                                disabled={submitting}
                                onClick={() => void setPrimary(row.id)}
                              >
                                设为主联系人
                              </Button>
                            ) : null}
                          </Space>
                        ),
                      },
                    ]
                  : []),
              ]}
            />
            {contactId ? (
              <Form
                name="customer-contact"
                form={contactForm}
                layout="vertical"
              >
                <Row gutter={12}>
                  <Col xs={24} sm={8}>
                    <Form.Item
                      name="name"
                      label={customerLabel('customer_contact', '姓名')}
                      rules={[
                        {
                          required:
                            contactId === 'new' ||
                            Boolean(
                              detail?.contacts
                                .find((item) => item.id === contactId)
                                ?.name.trim(),
                            ),
                          whitespace: true,
                        },
                      ]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Form.Item
                      name="phone"
                      label={customerLabel('customer_phone', '电话')}
                      rules={[
                        {
                          required: contactId === 'new',
                          pattern: PHONE,
                          message: '请输入合同允许的电话格式',
                        },
                      ]}
                    >
                      <Input
                        disabled={!canDetailPhoneWrite}
                        placeholder={
                          contactId !== 'new'
                            ? '掩码号码可留空不更新'
                            : undefined
                        }
                      />
                    </Form.Item>
                  </Col>
                  {contactId !== 'new' ? (
                    <Col xs={24} sm={8}>
                      <Form.Item
                        name="status"
                        label={customerLabel('customer_contact_status', '状态')}
                      >
                        <Select
                          options={[
                            { value: 'active', label: '启用' },
                            { value: 'inactive', label: '停用' },
                          ]}
                        />
                      </Form.Item>
                    </Col>
                  ) : null}
                </Row>
                <Space>
                  <Button onClick={() => setContactId(null)}>取消</Button>
                  <Button
                    type="primary"
                    loading={submitting}
                    onClick={() => void saveContact()}
                  >
                    保存联系人
                  </Button>
                </Space>
              </Form>
            ) : null}
          </>
        ) : null}
      </Drawer>
      <Modal
        open={duplicates.length > 0}
        destroyOnHidden
        title="发现可能重复客户"
        okText="确认仍创建新客户"
        cancelText="返回修改"
        confirmLoading={submitting}
        onCancel={() => setDuplicates([])}
        onOk={() => {
          setDuplicateConfirmed(true);
          setDuplicates([]);
          void saveCustomer(true);
        }}
      >
        <p>已有以下相似客户，是否仍创建新客户？</p>
        {duplicates.map((item) => (
          <div key={item.customerId}>
            {item.customerNo} · {item.name} · {item.contactPhone}
          </div>
        ))}
      </Modal>
    </div>
  );
}
